import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Typography from "../../../Utility/Typography";
import Checkbox from "../../../Utility/Checkbox/Checkbox";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import { ContentLoader } from "../../../Utility/Loader";
import NoDataFound from "../../../Utility/NoDataFound/NoDataFound";
import { RiCheckLine } from "react-icons/ri";
import {
  fetchPermissionsData,
  fetchRolePermissions,
  updateRolePermissions,
} from "../../../Redux/Thunks/permissionsThunk";
import {
  selectPermissionCategories,
  selectRolePermissions,
  selectPermissionsLoading,
  togglePermissionLocally,
  toggleCategoryLocally,
  setRolePermissionsLocally,
} from "../../../Redux/Slices/permissionsSlice";
import { selectRolesList } from "../../../Redux/Slices/rolesSlice";
import { fetchRolesData } from "../../../Redux/Thunks/rolesThunk";

const PermissionTab = ({ initialSelectedRole }) => {
  const dispatch = useDispatch();

  const categories = useSelector(selectPermissionCategories);
  const rolePermissions = useSelector(selectRolePermissions);
  const loading = useSelector(selectPermissionsLoading);
  const roles = useSelector(selectRolesList);

  const [selectedRole, setSelectedRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  /** Apply URL/table-driven initial role only once — do not reset dropdown when user picks another role. */
  const didApplyInitialRoleRef = useRef(false);

  useEffect(() => {
    dispatch(fetchPermissionsData());
    dispatch(fetchRolesData());
  }, [dispatch]);

  const selectedRoleObj = roles.find((r) => r.name === selectedRole);
  const selectedRoleId = selectedRoleObj?.id;
  const roleKey = selectedRoleId ?? selectedRole;

  useEffect(() => {
    if (roles.length === 0) return;

    if (initialSelectedRole) {
      if (!didApplyInitialRoleRef.current) {
        const name =
          typeof initialSelectedRole === "string"
            ? initialSelectedRole
            : initialSelectedRole?.name || "";
        setSelectedRole(name);
        didApplyInitialRoleRef.current = true;
      }
    } else if (!selectedRole) {
      setSelectedRole(roles[0].name);
    }
  }, [roles, initialSelectedRole, selectedRole]);

  useEffect(() => {
    if (!selectedRoleId) return;
    dispatch(fetchRolePermissions(selectedRoleId));
  }, [dispatch, selectedRoleId]);

  const roleNames = roles.map((role) => role.name);
  const currentRolePermissions = rolePermissions[roleKey] || [];

  const handleTogglePermission = (permissionId) => {
    dispatch(
      togglePermissionLocally({
        role: roleKey,
        permissionId,
      })
    );
  };

  const hasPermission = (permissionId) => {
    return currentRolePermissions.includes(permissionId);
  };

  const handleToggleCategory = (category) => {
    const categoryPermissionIds = category.permissions.map((p) => p.id);
    const allEnabled = categoryPermissionIds.every((id) => currentRolePermissions.includes(id));

    dispatch(
      toggleCategoryLocally({
        role: roleKey,
        categoryPermissionIds,
        enable: !allEnabled,
      })
    );
  };

  const isCategoryFullyEnabled = (category) => {
    const categoryPermissionIds = category.permissions.map((p) => p.id);
    return categoryPermissionIds.every((id) => currentRolePermissions.includes(id));
  };

  const isCategoryPartiallyEnabled = (category) => {
    const categoryPermissionIds = category.permissions.map((p) => p.id);
    const enabledCount = categoryPermissionIds.filter((id) =>
      currentRolePermissions.includes(id)
    ).length;
    return enabledCount > 0 && enabledCount < categoryPermissionIds.length;
  };

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    setShowSaveSuccess(false);
    try {
      await dispatch(
        updateRolePermissions({
          roleId: selectedRoleId,
          permissions: currentRolePermissions,
        })
      ).unwrap();

      dispatch(
        setRolePermissionsLocally({
          role: roleKey,
          permissions: currentRolePermissions,
        })
      );
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (e) {
      void e;
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
      <div className="bg-surface-primary border border-border-primary rounded-md shadow-sm p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Typography variant="text1" className="font-semibold text-text-primary">
              Permissions
            </Typography>
            <Typography variant="text2" className="text-text-secondary">
              Configure what each role can access and do
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <Typography variant="span1" className="text-text-secondary whitespace-nowrap">
              Select Role:
            </Typography>
            <Dropdown
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              options={roleNames}
              placeholder="Select Role"
              className="min-w-[180px]"
            />
          </div>
        </div>

        {showSaveSuccess && (
          <div className="flex items-center gap-2 p-3 bg-green-100 border border-green-300 rounded-md">
            <RiCheckLine className="text-green-600 text-lg" />
            <Typography variant="span1" className="text-green-700">
              Permissions for <strong>{selectedRole}</strong> saved successfully!
            </Typography>
          </div>
        )}

        {categories.length === 0 ? (
          !loading && <NoDataFound message="No permission categories found" />
        ) : (
          <>
            {selectedRole && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center">
                <Typography variant="span1" className="text-blue-700">
                  Editing permissions for: <strong>{selectedRole}</strong>
                  <Typography variant="span1" className="ml-2 text-blue-500" component="span">
                    ({currentRolePermissions.length} permissions selected)
                  </Typography>
                </Typography>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="border border-border-primary rounded-md p-4 bg-surface-secondary"
                >
                  <div
                    className="flex items-center gap-2 mb-3 pb-2 border-b border-border-primary cursor-pointer"
                    onClick={() => handleToggleCategory(category)}
                  >
                    <Checkbox
                      checked={isCategoryFullyEnabled(category)}
                      indeterminate={isCategoryPartiallyEnabled(category)}
                      onChange={() => handleToggleCategory(category)}
                      id={`category-${category.id}`}
                    />
                    <div>
                      <Typography variant="text2" className="font-semibold text-text-primary">
                        {category.name}
                      </Typography>
                      {category.description && (
                        <Typography variant="text3" className="text-text-secondary text-xs">
                          {category.description}
                        </Typography>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {category.permissions.map((permission) => (
                      <div key={permission.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={hasPermission(permission.id)}
                          onChange={() => handleTogglePermission(permission.id)}
                          id={permission.id}
                          label={permission.label}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-4 border-t border-border-primary">
              <FormSubmitButton
                type="button"
                onClick={handleSavePermissions}
                loading={saving}
                label="Save Permissions"
                loadingText="Saving..."
                idleIconClass=""
                size="md"
                className="px-6"
                disabled={!selectedRole}
              />
            </div>
          </>
        )}
      </div>
    </ContentLoader>
  );
};

export default PermissionTab;
